import { BaseQueryApi, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '@features/store';
import { ENV, eventEmitter } from '@utils';
import { logout } from '@features/auth/auth.slice';

type ArgsType = string | { url: string; method?: string; body?: unknown }
type ExtraOptionsType = Record<string, unknown>;

// Cache pour éviter de recréer fetchBaseQuery à chaque requête
let cachedBaseQuery: ReturnType<typeof fetchBaseQuery> | null = null;
let cachedCompanyApiUrl: string | null = null;
let cachedCompanyAppName: string | null = null;

const baseQuery = async (
    args: ArgsType,
    api: BaseQueryApi,
    extraOptions:  ExtraOptionsType
) => {
    const state = api.getState() as RootState;
    const companyAppName = state.company?.companyAppName || "ecs.energie-cote-sud.com";
    let companyApiUrl = ENV.API_URL;

    if(ENV.APP_ENV != 'local') {
        companyApiUrl = state.company?.companyApiUrl || ENV.API_URL;
    }

    // Réutiliser l'instance en cache si les paramètres n'ont pas changé
    if (
        !cachedBaseQuery ||
        cachedCompanyApiUrl !== companyApiUrl ||
        cachedCompanyAppName !== companyAppName
    ) {
        cachedBaseQuery = fetchBaseQuery({
            baseUrl: companyApiUrl,
            prepareHeaders: (headers: Headers, { getState }: Pick<BaseQueryApi, 'getState' | 'extra' | 'endpoint' | 'type' | 'forced'>) => {
                const token = (getState() as RootState).authentication.user?.token;

                if (token) {
                    headers.set('x-ap-rawtoken', token);
                }

                headers.set('x-ap-resty-appname', companyAppName);

                return headers;
            }
        });

        cachedCompanyApiUrl = companyApiUrl;
        cachedCompanyAppName = companyAppName;
    }

    return cachedBaseQuery(args, api, extraOptions);
};

const baseQueryWithLogout = async (args: ArgsType, api: BaseQueryApi, extraOptions: ExtraOptionsType) => {
    const result = await baseQuery(args, api, extraOptions);

    if (result.error && 'status' in result.error && result.error.status === 403) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorMessageTranslationKey = (result.error as any)?.data?.error?.message;

        if (errorMessageTranslationKey) {
            eventEmitter.emit('error', errorMessageTranslationKey);
        }

        if (errorMessageTranslationKey === 'invalid_token') {
            api.dispatch(logout());
        }
    }

    if (result.error && result.error.status === 500) {
        eventEmitter.emit('error', 'internal_server_error');
    }

    return result;
};

export const ecsApi = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithLogout,
    endpoints: () => ({})
});
